import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Lock, Eye, ScrollText, FileText, Heart, Activity, Building2,
  CheckCircle2, ArrowLeft, ExternalLink, Mail, Accessibility as AccessibilityIcon,
  Database, Globe, BookOpen, Download, Bot,
} from 'lucide-react';
import PageMeta from '../components/PageMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public Trust Center — the single B2B procurement destination.
 *
 * Schools, corporates, journalists, security teams and partners all want to
 * verify the same things before they engage with HiBFF. This page aggregates
 * every credibility artefact we publish — status, security, privacy,
 * compliance, accessibility — into one screenshot-ready URL they can paste
 * into their internal questionnaires.
 *
 * Distinct from /trust (which is live engagement social-proof for parents).
 */
const TrustCenter = () => {
  const [overall, setOverall] = useState(null);
  const [robustness, setRobustness] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/status/summary`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        setOverall(d.overall);
        if (d.robustness?.available) setRobustness(d.robustness);
      })
      .catch(() => {});
  }, []);

  const statusBadgeColor = overall === 'operational' ? '#10B981'
    : overall === 'degraded' ? '#F59E0B'
    : overall === 'outage' ? '#EF4444'
    : '#9CA3AF';

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white" data-testid="trust-center-page">
      <PageMeta
        slug="trust-center"
        path="/trust-center"
        title="Trust Center"
        description="Verified, transparent, audit-ready. Live status, security policy, privacy, accessibility, subprocessors, SLA — all in one B2B procurement-ready destination."
      />
      <header className="px-6 sm:px-12 py-6 max-w-6xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900" data-testid="trust-center-back-home">
          <ArrowLeft size={18} /> <span className="font-medium">HiBFF home</span>
        </Link>
      </header>

      <main className="px-6 sm:px-12 max-w-6xl mx-auto pb-20">
        {/* Hero */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-sm font-medium text-purple-600 uppercase tracking-wide mb-2">
              <Shield size={14} className="inline -mt-0.5 mr-1" /> Trust Center
            </p>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold leading-tight" style={{ color: '#1A1209' }}>
              Built for trust. Verified, transparent, audit-ready.
            </h1>
          </div>
          <Link
            to="/status"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white font-medium text-sm transition self-start sm:self-auto"
            style={{ backgroundColor: statusBadgeColor }}
            data-testid="trust-center-status-pill"
          >
            <Activity size={16} />
            {overall === 'operational' ? 'All systems operational'
              : overall === 'degraded' ? 'Degraded performance'
              : overall === 'outage' ? 'Outage'
              : 'Live status'}
          </Link>
        </div>

        <p className="text-base text-gray-700 max-w-3xl mb-8">
          HiBFF is used by parents, teens, schools, and corporate wellbeing teams.
          We treat that responsibility seriously. Everything below — uptime, privacy,
          security, accessibility, compliance — is verifiable by you, today, without
          needing to email anyone.
        </p>

        {/* ROBOT robustness badge — Sprint 228 */}
        {robustness && (
          <Link
            to="/status"
            className="block mb-12 rounded-2xl border-2 p-5 sm:p-6 transition hover:shadow-md"
            style={{ borderColor: robustness.healthy ? '#10B981' : '#F59E0B', backgroundColor: robustness.healthy ? '#ECFDF5' : '#FFFBEB' }}
            data-testid="trust-center-robustness-badge"
          >
            <div className="flex items-start sm:items-center justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3 flex-1 min-w-[240px]">
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                  style={{ backgroundColor: robustness.healthy ? '#10B981' : '#F59E0B' }}>
                  <Bot size={20} className="text-white" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-semibold mb-0.5"
                    style={{ color: robustness.healthy ? '#059669' : '#B45309' }}>
                    Web Platform Robustness · ROBOT
                  </p>
                  <p className="text-xl sm:text-2xl font-bold tabular-nums" style={{ color: '#1A1209' }} data-testid="trust-center-robustness-label">
                    {robustness.label}
                  </p>
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                    Programmatically checked on every PR <strong>+ nightly at 04:00 UTC</strong> for layout overflow, visual regressions, and 40×40px tap targets.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 text-sm font-semibold whitespace-nowrap"
                style={{ color: robustness.healthy ? '#059669' : '#B45309' }}>
                See live status <ExternalLink size={14} />
              </div>
            </div>
          </Link>
        )}

        {/* Card grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          <TrustCard
            icon={<Activity size={20} />}
            title="Live status"
            desc="Real-time uptime, component-by-component, last 90 days of incidents."
            href="/status"
            cta="Open status page"
            tid="trust-card-status"
          />
          <TrustCard
            icon={<Shield size={20} />}
            title="Security policy"
            desc="Responsible-disclosure policy, scope, safe-harbour, hall of fame."
            href="https://github.com/hibff/hibff/security/policy"
            cta="Read policy"
            external
            tid="trust-card-security"
          />
          <TrustCard
            icon={<Lock size={20} />}
            title="Privacy policy"
            desc="What we collect, why, who we share with, and your rights under GDPR/CCPA/COPPA."
            href="/privacy"
            cta="Read privacy"
            tid="trust-card-privacy"
          />
          <TrustCard
            icon={<ScrollText size={20} />}
            title="Terms of Service"
            desc="The agreement that governs your use of HiBFF, including B2B licensing."
            href="/terms"
            cta="Read terms"
            tid="trust-card-terms"
          />
          <TrustCard
            icon={<AccessibilityIcon size={20} />}
            title="Accessibility"
            desc="WCAG 2.1 AA · UK Public Sector Bodies Regulations · EAA. Reporting workflow."
            href="/accessibility"
            cta="Statement"
            tid="trust-card-accessibility"
          />
          <TrustCard
            icon={<Database size={20} />}
            title="Subprocessors"
            desc="The full list of vendors that process HiBFF data (Postmark, Twilio, Atlas, etc)."
            href="/subprocessors"
            cta="View list"
            tid="trust-card-subprocessors"
          />
          <TrustCard
            icon={<CheckCircle2 size={20} />}
            title="Service-level commitment"
            desc="Our 99.9 % uptime target, last 12 months actuals, compensation policy."
            href="/sla"
            cta="View SLA"
            tid="trust-card-sla"
          />
          <TrustCard
            icon={<Heart size={20} />}
            title="Teen Charter"
            desc="The promises we make directly to the teens who use AvA."
            href="/charter"
            cta="Read charter"
            tid="trust-card-charter"
          />
          <TrustCard
            icon={<FileText size={20} />}
            title="security.txt"
            desc="RFC 9116 — discoverable security contact for scanners and researchers."
            href="/.well-known/security.txt"
            cta="View security.txt"
            external
            tid="trust-card-securitytxt"
          />
          <TrustCard
            icon={<FileText size={20} />}
            title="Postmortems"
            desc="Public RFC-style write-ups of any service incidents we've had."
            href="/postmortems"
            cta="View postmortems"
            tid="trust-card-postmortems"
          />
          <TrustCard
            icon={<Mail size={20} />}
            title="Contact us"
            desc="Procurement, security, partnerships, press — all routed to a real human."
            href="/contact"
            cta="Get in touch"
            tid="trust-card-contact"
          />
        </div>

        {/* Compliance roadmap */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 mb-10">
          <h2 className="text-base sm:text-lg font-semibold mb-5" style={{ color: '#1A1209' }}>Compliance & certifications</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
            <ComplianceRow status="active"   name="UK GDPR" desc="Data Protection Act 2018, ICO-registered." />
            <ComplianceRow status="active"   name="EU GDPR" desc="EU Representative arrangement; lawful basis documented per processing activity." />
            <ComplianceRow status="active"   name="COPPA" desc="Parent consent, age-gated AvA personality, US-specific data minimisation." />
            <ComplianceRow status="active"   name="WCAG 2.1 Level AA" desc="Tested with NVDA, VoiceOver, axe DevTools, manual keyboard pass." />
            <ComplianceRow status="active"   name="CCPA / CPRA" desc="California consumer rights honoured globally; do-not-sell respected." />
            <ComplianceRow status="active"   name="Secure-by-default" desc="HTTPS everywhere, JWT auth, rate-limited APIs, MongoDB Atlas at-rest encryption." />
            <ComplianceRow status="roadmap"  name="SOC 2 Type II" desc="Evidence collection in progress (audit logs, change management, vendor reviews)." />
            <ComplianceRow status="roadmap"  name="ISO 27001" desc="Mapping 27002 controls; targeting 2026 certification." />
            <ComplianceRow status="roadmap"  name="IMDA / PDPA Singapore" desc="Onboarding kit drafted for Singapore B2B partners." />
          </div>
        </section>

        {/* GDPR self-service */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 mb-10">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <h2 className="text-base sm:text-lg font-semibold mb-1" style={{ color: '#1A1209' }}>Your data, your control</h2>
              <p className="text-sm text-gray-700">
                Already a HiBFF user? Download every byte we hold on you, or
                trigger the full erasure workflow — both right from your account
                settings, no email tickets required.
              </p>
            </div>
            <Link
              to="/settings"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition text-sm font-semibold"
              data-testid="trust-center-data-controls"
            >
              <Download size={16} /> Open settings
            </Link>
          </div>
        </section>

        {/* Embeddable badge */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 mb-10">
          <h2 className="text-base sm:text-lg font-semibold mb-3" style={{ color: '#1A1209' }}>Embed our live status</h2>
          <p className="text-sm text-gray-700 mb-4">
            Drop the live status badge into your pitch deck, partner page, or README.
            It&apos;s a 200-byte SVG that updates automatically and links straight to the
            full status page.
          </p>
          <a href="/api/status/badge.svg" target="_blank" rel="noopener noreferrer">
            <img src={`${API_URL}/api/status/badge.svg?ts=${Date.now()}`} alt="HiBFF live status badge" className="rounded mb-4" />
          </a>
          <pre className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs overflow-x-auto" data-testid="trust-badge-snippet">
{`<a href="https://hibff.com/status">
  <img src="https://hibff.com/api/status/badge.svg" alt="HiBFF status" />
</a>`}
          </pre>
        </section>

        {/* Contact */}
        <section className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border border-purple-100 p-6 sm:p-8 text-center">
          <h2 className="text-base sm:text-lg font-semibold mb-2" style={{ color: '#1A1209' }}>Procurement, security, or compliance question?</h2>
          <p className="text-sm text-gray-700 mb-4">
            We answer enterprise security questionnaires within 5 business days.
            Bug reports get a response within 2.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <a href="mailto:security@hibff.com" className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition text-sm font-semibold" data-testid="trust-center-email-security">
              <Mail size={14} /> security@hibff.com
            </a>
            <a href="mailto:business@hibff.com" className="inline-flex items-center gap-2 px-4 py-2 border border-purple-300 text-purple-700 rounded-full hover:bg-white transition text-sm font-semibold" data-testid="trust-center-email-procurement">
              <Building2 size={14} /> business@hibff.com
            </a>
          </div>
        </section>
      </main>
    </div>
  );
};

const TrustCard = ({ icon, title, desc, href, cta, external, tid }) => {
  const Comp = external ? 'a' : Link;
  const props = external ? { href, target: '_blank', rel: 'noopener noreferrer' } : { to: href };
  return (
    <Comp
      {...props}
      className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 hover:shadow-md hover:border-purple-200 transition flex flex-col"
      data-testid={tid}
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 bg-purple-50 text-purple-600">
        {icon}
      </div>
      <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-600 mb-4 flex-1">{desc}</p>
      <span className="text-sm font-medium text-purple-600 inline-flex items-center gap-1">
        {cta} {external ? <ExternalLink size={12} /> : <ArrowLeft size={12} className="rotate-180" />}
      </span>
    </Comp>
  );
};

const ComplianceRow = ({ status, name, desc }) => {
  const meta = status === 'active'
    ? { color: '#10B981', label: 'Active', icon: CheckCircle2 }
    : { color: '#F59E0B', label: 'Roadmap', icon: BookOpen };
  const Icon = meta.icon;
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5" style={{ color: meta.color }}>
        <Icon size={18} />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">{name}</span>
          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ backgroundColor: `${meta.color}1a`, color: meta.color }}>
            {meta.label}
          </span>
        </div>
        <p className="text-xs text-gray-600 mt-0.5">{desc}</p>
      </div>
    </div>
  );
};

export default TrustCenter;
